import type { LineItemType as LineItemTypeFromDB } from "@prisma/client";
import { z } from "zod";
import { type ComplexityType, ComplexityTypeSchema } from "@remi/enums/complexity-type";
import {
  type FinanciallyResponsibleParty,
  FinanciallyResponsiblePartySchema,
} from "@remi/enums/financially-responsible-party";
import { InvoicePartySchema, InvoicePartyValues } from "@remi/enums/invoice-party";
import { type LineItemType, LineItemTypeSchema, LineItemTypeValues } from "@remi/enums/line-item-type";
import { PricingTypeSchema } from "@remi/enums/pricing-type";
import { ProjectCategory, ProjectCategorySchema } from "@remi/enums/project-category";
import { PvSystemOverridePriceTypeSchema } from "@remi/enums/pv-system-override-price-type";
import { type PvSystemProvider, PvSystemProviderSchema } from "@remi/enums/pv-system-provider";
import type { Json } from "@remi/ts-utils/json";

import { GeoJSONFeatureCollectionSchema, GeoJSONFeatureSchema } from "./geojson";
import { QuoteStatusSchema } from "./quoteStatus";
import { PrevRoofMaterialType, RoofMaterialType } from "./roofMaterial";
import { StateSchema } from "./state";

// Deliberate local copy — quoting-v3 must stay independent of the extracted enums modules
const SalesDiscountLineItemTypeValues = [
  "Price Match Discount",
  "Courtesy Discount",
  "Deal Desk Discount",
  "Other Discount",
  "Sales Adjustment",
  "Non Sales Adjustment",
] as const satisfies readonly LineItemType[];
const SalesDiscountLineItemType = z.enum(SalesDiscountLineItemTypeValues).enum;

// Sales Upgrades Schemas (moved here to avoid circular dependency)
const salesUpgradeSchema = z.object({
  available: z.boolean(),
  isEnabled: z.boolean(),
});

export type SalesUpgrade = z.infer<typeof salesUpgradeSchema>;

const salesUpgradesInputSchema = z.object({
  critterGuard: salesUpgradeSchema.nullable(),
  extendedWarranty: salesUpgradeSchema.nullable(),
});

export type SalesUpgradesInput = z.infer<typeof salesUpgradesInputSchema>;

const PvSystemQuotingInputItemSchema = z.object({
  id: z.string().nullish(),
  removalPanels: z.number(),
  reinstallPanels: z.number(),
  provider: PvSystemProviderSchema,
  overridePrice: z.number().nullable(),
  overridePriceType: PvSystemOverridePriceTypeSchema,
  preSignedHic: z.boolean(),
  name: z.string(),
  financiallyResponsibleParty: FinanciallyResponsiblePartySchema,
  permitCost: z.number(),
  redesignFee: z.number(),
  numPanelDisposal: z.number(),
  numSolarBracketRemoval: z.number(),
  removalGroundEquipmentNeeded: z.boolean(),
  reinstallGroundEquipmentNeeded: z.boolean(),
});

const PvSystemQuotingInputSchema = z.array(PvSystemQuotingInputItemSchema);

const freezeLineItemPriceDataSchema = z.object({
  enabled: z.boolean(),
  date: z.date().nullable(),
});

export const BidLineItemOutputSchema = z.object({
  quantity: z.number(),
  unitPrice: z.number(),
  customName: z.string().nullable(),
  customDescription: z.string().nullable(),
  customMarginPercentage: z.number().nullable(),
  customPricePreMargin: z.number().nullable(),
  customUnit: z.string().nullable(),
  lineItemTypeName: LineItemTypeSchema,
  pricingType: PricingTypeSchema,
  visibleOnQuote: z.boolean().default(true),
  lineItemType: z.object({
    name: LineItemTypeSchema,
    pricingType: PricingTypeSchema,
  }),
  customProjectCategory: z.enum(ProjectCategory).nullable(),
  shouldInvoice: z.boolean().default(true),
  pvSystemName: z.string().nullish(),
  pvSystem: z
    .object({
      name: z.string(),
    })
    .nullish(),
  pvSystemId: z.string().nullish(),
  invoiceParty: InvoicePartySchema,
  roofSectionId: z.string().nullable(),
  roofLineId: z.string().nullable(),
  changeOrderId: z.string().nullish(),
});

const RoofSectionInputSchema = z.object({
  id: z.string(),
  bidQuoteId: z.string(),
  geoJson: GeoJSONFeatureSchema,
  financiallyResponsibleParty: FinanciallyResponsiblePartySchema,
  previousRoofMaterial: z.enum(PrevRoofMaterialType),
  roofMaterialBeingInstalled: z.enum(RoofMaterialType),
  isMansardRoof: z.boolean(),
  isAsphaltOverlayInstallation: z.boolean(),
  isRepair: z.boolean().optional().default(false),
});

export type RoofSectionInput = z.infer<typeof RoofSectionInputSchema>;

const RoofLineInputSchema = z.object({
  id: z.string(),
  bidQuoteId: z.string(),
  geoJson: GeoJSONFeatureSchema,
  financiallyResponsibleParty: FinanciallyResponsiblePartySchema,
  isGutterGuard: z.boolean(),
  isGutterInstallation: z.boolean(),
  isGutterRemoval: z.boolean(),
  gutterSize: z.string().nullable(),
  isCutInRidgeVent: z.boolean(),
  isStepFlashing: z.boolean(),
  isTilePanFlashing: z.boolean(),
  isCritterGuard: z.boolean(),
  isFaciaBoarding: z.boolean(),
  isStarterShingle: z.boolean(),
  isHipAndRidgeCap: z.boolean(),
  previousRoofMaterial: z.enum(PrevRoofMaterialType),
  roofMaterialBeingInstalled: z.enum(RoofMaterialType),
  isAsphaltOverlayInstallation: z.boolean(),
});

export type RoofLineInput = z.infer<typeof RoofLineInputSchema>;

export type BidLineItemOutput = z.infer<typeof BidLineItemOutputSchema>;

const QuoteInputSchema = z.object({
  geoJSON: GeoJSONFeatureCollectionSchema,
  customLineItems: z.array(BidLineItemOutputSchema),
  salesDiscountInput: z
    .object({
      discountType: z.enum(SalesDiscountLineItemType),
      discountAmount: z.number(),
      nonSalesDiscountAmount: z.number().optional().default(0),
    })
    .nullable(),
  roofMaterialBeingInstalled: z.enum(RoofMaterialType),
  previousRoofMaterial: z.enum(PrevRoofMaterialType),
  imageId: z.string().nullable(),
  sketchFiles: z.array(z.object({ id: z.string(), name: z.string() })),
  files: z.array(z.object({ id: z.string(), url: z.string() })),
  notes: z.string().nullable(),
  productionNotes: z.string().nullable(),
  customHeader: z.string().nullable(),
  expectedArea: z.number().nullable(),
  expectedPitchDegrees: z.number().nullable(),
  isPartialRoofWork: z.boolean(),
  isAsphaltOverlayInstallation: z.boolean().nullable(),
  numberOfLayersPresent: z.number(),
  houseStoriesTall: z.number(),
  isDeckingBeingInstalled: z.boolean(),
  isDeckingAreaSameAsRoofArea: z.boolean(),
  areaOfDeckingBeingInstalled: z.number(),
  ventCount: z.number(),
  dormerVentCount: z.number(),
  foamPenetrationCount: z.number(),
  rafterCount: z.number(),
  purlinCount: z.number(),
  ridgeCapLength: z.number(),
  turbineVentCount: z.number(),
  lichenAndMossArea: z.number(),
  solarThermalRemovalCount: z.number(),
  fasciaBoardLength: z.number(),
  numberOfPanelsPendingRemoval: z.number(),
  numberOfPanelsPendingReinstall: z.number(),
  hasPriceGuarantee: z.boolean(),
  freezeLineItemPriceData: freezeLineItemPriceDataSchema.nullable(),
  hideWarrantyInfo: z.boolean(),
  state: StateSchema,
  pvSystems: PvSystemQuotingInputSchema,
  dealerFee: z.number().nullable(),
  roofSections: z.array(RoofSectionInputSchema),
  roofLines: z.array(RoofLineInputSchema),
  roofComplexity: ComplexityTypeSchema,
  isFullCustomQuote: z.boolean(),
  isManualShingleLift: z.boolean(),
  isDoubleHandling: z.boolean(),
  numFeltLayers: z.number(),
  skylightCount: z.number(),
  skylightRemovalCount: z.number(),
  hingePlateOrCollarTieCount: z.number(),
  numChimneys: z.number(),
  numTilesToBeSourced: z.number(),
  isRll: z.boolean(),
  hipAndRidgeCapLength: z.number(),
  starterShingleLength: z.number(),
  isReadyForAudit: z.boolean(),
  hasExtendedWorkmanshipWarranty: z.boolean().nullable(),
  salesUpgrades: salesUpgradesInputSchema.nullable().optional(),
  isSiteVisitNeeded: z.boolean(),
  isItelRequired: z.boolean().default(false),
  siteVisitRepairsCost: z.number(),
  isAdjoiningStructure: z.boolean(),
  currentQuote: z
    .object({
      id: z.string(),
      pdfId: z.string().nullable(),
      image: z.object({ id: z.string(), name: z.string() }),
      sketchFiles: z.array(z.object({ id: z.string(), name: z.string() })),
      status: QuoteStatusSchema,
      submittedAmount: z.number(),
      numberOfPanelsPendingRemoval: z.number(),
      numberOfPanelsPendingReinstall: z.number(),
      hasPriceGuarantee: z.boolean(),
      hideWarrantyInfo: z.boolean(),
      pvSystems: PvSystemQuotingInputSchema,
      expiresAt: z.date().nullable(),
      quoteLineItems: z.array(
        z.object({
          pricingType: PricingTypeSchema,
          lineItemTypeName: LineItemTypeSchema,
          customProjectCategory: z.enum(ProjectCategory).nullable(),
          unitPrice: z.number(),
          quantity: z.number(),
          visibleOnQuote: z.boolean().optional().default(true),
          lineItemType: z.object({
            name: LineItemTypeSchema,
            pricingType: PricingTypeSchema,
          }),
          customName: z.string().nullable(),
          customDescription: z.string().nullable(),
          customPricePreMargin: z.number().nullable(),
          customMarginPercentage: z.number().nullable(),
          customUnit: z.string().nullable(),
          shouldInvoice: z.boolean().default(true),
          invoiceParty: InvoicePartySchema,
          pvSystemName: z.string().nullish(),
          roofSectionId: z.string().nullable(),
          roofLineId: z.string().nullable(),
        }),
      ),
    })
    .nullable(),
});
export type QuoteInput = z.infer<typeof QuoteInputSchema>;
export type PvSystemInputField = keyof QuoteInput["pvSystems"][number];
// TODO: We should get rid of this in the future and make it nullable wherever it's used
export const defaultSalesDiscountInput = {
  discountAmount: 0,
  discountType: SalesDiscountLineItemType["Sales Adjustment"],
  nonSalesDiscountAmount: 0,
};

export const FinalizeQuoteFormSchema = z.object({
  projects: z.array(
    z.object({
      category: ProjectCategorySchema,
      merge: z.boolean(),
      lineItems: z.array(
        z.object({
          projectId: z.number().nullable(),
          lineItemTypeName: z.enum(LineItemTypeValues, {
            error: "Select a line item",
          }),
          pricingType: PricingTypeSchema,
          quantity: z.coerce.number(),
          unitPrice: z.coerce.number(),
          invoiceParty: z.enum(InvoicePartyValues, {
            error: "Select an invoice party",
          }),
          customName: z.string().nullable(),
          pvSystemName: z.string().nullable(),
          roofSectionId: z.string().nullable(),
          roofLineId: z.string().nullable(),
          // Preserves Remi-hidden CO residual rows (shouldInvoice=false,
          // visibleOnQuote=false) through the finalize step. Without these,
          // handle-quote.finalizeQuote force-promotes every line to
          // visibleOnQuote=true, which billed the 1.42 sq residual to
          // the HOMEOWNER placeholder. Optional so legacy callers still work.
          visibleOnQuote: z.boolean().optional(),
          shouldInvoice: z.boolean().optional(),
          changeOrderId: z.string().nullable().optional(),
          customPricePreMargin: z.number().nullable().optional(),
        }),
      ),
    }),
  ),
});
export const FinalizeQuoteInputSchema = FinalizeQuoteFormSchema.extend(
  z.object({ bidQuoteId: z.string().optional(), quoteVersionId: z.string().optional() }).shape,
).refine((data) => Boolean(data.bidQuoteId) !== Boolean(data.quoteVersionId), {
  error: "Exactly one of bidQuoteId or quoteVersionId must be provided",
});
export type FinalizeQuoteInput = z.infer<typeof FinalizeQuoteInputSchema>;

export const BidRequestDataSchema = z
  .object({
    houseStoriesTall: z.number().optional(),
    numberOfLayersPresent: z.number().optional(),
    isPartialRoofWork: z.boolean().optional(),
    previousRoofMaterial: z.enum(PrevRoofMaterialType).optional(),
    roofMaterialBeingInstalled: z.enum(RoofMaterialType).optional(),
    isAsphaltOverlayInstallation: z.boolean().optional(),
    isDeckingAreaSameAsRoofArea: z.boolean().optional(),
    threeInchVentCount: z.number().optional(),
    fourInchVentCount: z.number().optional(),
    dormerVentCount: z.number().optional(),
    turbineVentCount: z.number().optional(),
    solarThermalRemovalCount: z.number().optional(),
    skylightRemovalCount: z.number().optional(),
    foamPenetrationCount: z.number().optional(),
    rafterCount: z.number().optional(),
    purlinCount: z.number().optional(),
    fasciaBoardLength: z.number().optional(),
    expectedArea: z.number().optional(),
    expectedPitchDegrees: z.number().optional(),
    numberOfPanelsPendingRemoval: z.number().optional(),
    numberOfPanelsPendingReinstall: z.number().optional(),
  })
  .nullable();

export const GetQuotingDataSchema = z.object({
  quoteVersionId: z.string().optional(),
  requestForQuoteId: z.number(),
});

export const GetLineItemPriceMapSchema = z.object({
  requestForQuoteId: z.number(),
  asOfDate: z.date().optional(),
});

export type GetQuotingData = z.infer<typeof GetQuotingDataSchema>;

export type PvSystemWithEnum = {
  name: string;
  removalPanels: number;
  reinstallPanels: number;
  overridePrice: number | null;
  preSignedHic: boolean;
  redesignFee: number;
  permitCost: number;
  provider: PvSystemProvider;
  financiallyResponsibleParty: FinanciallyResponsibleParty;
  numPanelDisposal: number;
  numSolarBracketRemoval: number;
  removalGroundEquipmentNeeded: boolean;
  reinstallGroundEquipmentNeeded: boolean;
};
export type QuoteFromDB = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt: Date | null;
  expiresAt: Date | null;
  imageId: string;
  pdfId: string | null;
  createdById: string;
  activeHicId: string | null;
  parentBidQuoteId: string | null;
  hasPriceGuarantee: boolean;
  roofMaterialBeingInstalled: string;
  previousRoofMaterial: string;
  isPartialRoofWork: boolean;
  houseStoriesTall: number;
  numberOfLayersPresent: number;
  isAsphaltOverlayInstallation: boolean;
  isDeckingBeingInstalled: boolean;
  isDeckingAreaSameAsRoofArea: boolean;
  areaOfDeckingBeingInstalled: number;
  areaOfDeckingBeingInstalledWithWaste: number;
  fasciaBoardLength: number;
  ventCount: number;
  dormerVentCount: number;
  foamPenetrationCount: number;
  rafterCount: number;
  purlinCount: number;
  ridgeCapLength: number;
  turbineVentCount: number;
  lichenAndMossArea: number;
  solarThermalRemovalCount: number;
  numberOfPanelsPendingRemoval: number;
  numberOfPanelsPendingReinstall: number;
  geoJSON: Json;
  customHeader: string | null;
  notes: string;
  submittedAmount: number;
  status: string;
  areaSquaresNoWaste: number;
  areaSquaresWithWaste: number;
  averagePitchFactor: number;
  flatArea: number;
  flatAreaWithWaste: number;
  pitchedArea: number;
  pitchedAreaWithWaste: number;
  expectedArea: number | null;
  expectedPitchDegrees: number | null;
  hideWarrantyInfo: boolean;
  dealerFee: number | null;
  isFullCustomQuote: boolean;
  roofComplexity: ComplexityType;
  requestForQuoteId: number;
  estimateId: string | null;
  isManualShingleLift: boolean;
  isDoubleHandling: boolean;
  isReadyForAudit: boolean;
  numFeltLayers: number;
  skylightCount: number;
  skylightRemovalCount: number;
  isRll: boolean;
  pvSystems: PvSystemWithEnum[];
  roofSections: RoofSectionInput[];
  roofLines: RoofLineInput[];
  hingePlateOrCollarTieCount: number;
  numChimneys: number;
  numTilesToBeSourced: number;
  isMansardRoof?: boolean; // this is only used for calculation, it will never be in the DB. But it is needed for the roof section calculation
  starterShingleLength: number;
  hipAndRidgeCapLength: number;
  hasExtendedWorkmanshipWarranty: boolean | null;
  isSiteVisitNeeded: boolean;
  isItelRequired: boolean;
  siteVisitRepairsCost: number;
  isAdjoiningStructure: boolean;
};

export type LineItemTypeWithEnumName = Omit<LineItemTypeFromDB, "id" | "name" | "createdAt" | "updatedAt"> & {
  name: LineItemType;
};
